import { Auth } from 'aws-amplify'
import { awsconfig, trialAwsConfig } from './../amplify'
import { AppDispatch, AuthState } from './../types/auth'
import { AnyAction } from 'redux'
import { CognitoUser } from '@aws-amplify/auth'
import { axiosJOTA, ProfileAPI } from '../api'
import { ErrorAuthType } from '../../libs/interfaces/Authentication.interface'
import { tokenType } from '../../libs/interfaces/User.interface'
import { User, getUserTrial } from '../../libs/model/user'
import { JU_TOKEN } from '@App/libs/utils/constants/global'
import { useFormTrialStore } from '@App/zustand/store/form-trial.store'
import { vercelLog } from '@App/libs/utils/geral'
import { clearAllStorageCognitoAmplify } from '@App/libs/utils/constants/cognito'

Auth.configure(awsconfig)

/**
 * Redux Duck Pattern
 * Authorization
 */
const initialState: AuthState = {
    authState: 'logout',
    user: null,
    error: null,
    loading: false,
    userBO: null,
    isProUser: false
}

//
// Reducers - Redux Duck Pattern
//
const AuthDuck = (
    state: AuthState = initialState,
    action: AnyAction = { type: '' }
): AuthState => {
    switch (action.type) {
        /**
         * Auth System Error
         * Description: Error on Cognito Authentication
         */
        case 'AUTH_SYSTEM_ERROR':
            return {
                ..._getCommonState(state),
                error: `AUTH_SYSTEM_ERROR ${action.payload}`,
                loading: false
            }

        /**
         * Auth Begin Loading
         * Description: Starts process of authentication on Cognito set loading is true
         */
        case 'AUTH_BEGIN_LOADING':
            return {
                ..._getCommonState(state),
                loading: true
            }

        /**
         * Auth Init
         * Description: Start auth with any error
         */
        case 'AUTH_INIT':
            return {
                ...initialState,
                error: action.payload,
                authState: action.payload
            }

        /**
         * Auth Change Auth State
         * Description: Detect any change of user or auth from Cognito
         */
        case 'AUTH_CHANGE_AUTH_STATE':
            return {
                ..._getCommonState(state),
                authState: action.payload
            }

        /**
         * Auth Sign In Success
         * Description: Auth Success - receive id token
         */
        case 'AUTH_SIGN_IN_SUCCESS':
            return _getUserSuccess(state, action)

        /**
         * Auth Forgot Password Success
         */
        case 'AUTH_FORGOT_PASSWORD_SUCCESS':
            return {
                ..._getCommonState(state),
                authState: 'forgotPasswordReset'
            }

        /**
         * Auth Forgot Password Error
         */
        case 'AUTH_FORGOT_PASSWORD_ERROR':
            return {
                ..._getCommonState(state),
                error: `AUTH_FORGOT_PASSWORD_ERROR ${action.payload}`,
                loading: false,
                authState: 'forgotPasswordError'
            }

        /**
         * Auth Fetch authed User
         * Description: Get user authenticated on Cognito
         */
        case 'AUTH_FETCH_AUTHED_USER':
            return _getUserSuccess(state, action)

        case 'AUTH_REFRESH_TOKEN': {
            const { trialProActive } = getUserTrial(action.payload.userBO)
            const isProUser =
                action.payload.userBO.profile.has_tributos ||
                action.payload.userBO.profile.has_poder ||
                action.payload.userBO.profile.has_saude ||
                trialProActive
            const idToken = action.payload.token.idToken.jwtToken
            const storageJOTAToken = {
                idToken,
                email: state.user?.getEmail()
            }
            localStorage.setItem(JU_TOKEN, JSON.stringify(storageJOTAToken))

            return {
                ..._getCommonState(state),
                user: new User(action.payload.user, action.payload.userBO)
                    .setToken(action.payload.token)
                    .getUser(),
                authState: 'user_logged',
                loading: false,
                isProUser
            }
        }
        case 'AUTH_SSO_SIGN_IN_SUCCESS': {
            const { idToken, email, userBO } = action.payload
            const { trialProActive } = getUserTrial(userBO)
            const isProUser = !!(
                userBO?.profile?.has_tributos ||
                userBO?.profile?.has_poder ||
                userBO?.profile?.has_saude ||
                trialProActive
            )

            return {
                ..._getCommonState(state),
                user: new User(null, userBO, { idToken, email }).getUser(),
                userBO: userBO ?? {},
                authState: 'user_logged',
                loading: false,
                isProUser,
                error: ''
            }
        }

        default:
            return state
    }
}

export default AuthDuck

const _getCommonState = (state: AuthState) => ({
    ...state,
    error: '',
    loading: false
})

const _getUserSuccess = (
    state: AuthState,
    action: AnyAction = { type: '' }
) => ({
    ..._getCommonState(state),
    user: new User(action.payload).getUser(),
    authState: 'user_logged',
    loading: false,
    error: ''
})

//
// Actions - Redux Duck Pattern
//
export type Actions = ReturnType<
    | typeof authForgotPasswordSuccess
    | typeof forgotPasswordChangeSuccess
    | typeof forgotPasswordChangeError
    | typeof authInit
    | typeof authBeginLoading
    | typeof authError
    | typeof changeAuthState
    | typeof authSignInSuccess
    | typeof authFirstLogin
    | typeof fetchAuthedUserSuccess
    | typeof updateUserBO
>

/**
 * Auth Forgot Password - Success
 * @param {string} email
 * @return {object} payload
 */
export const authForgotPasswordSuccess = (email: string) => ({
    type: 'AUTH_FORGOT_PASSWORD_SUCCESS',
    payload: email
})

/**
 * Forgot Passowrd Change - Success
 * @return {object}
 */
export const forgotPasswordChangeSuccess = () => ({
    type: 'AUTH_FORGOT_PASSWORD_SUCCESS',
    payload: 'verificar'
})

/**
 * Forgot Password Change Error
 * @param {any} err
 * @return {object}
 */
export const forgotPasswordChangeError = (err: string) => ({
    type: 'AUTH_FORGOT_PASSWORD_ERROR',
    payload: err
})

/**
 * Auth Init
 * @param {string} status
 * @return {object}
 */
export const authInit = (status: string) => ({
    type: 'AUTH_INIT',
    payload: status
})

/**
 * Auth Begin Loading
 * @return {object}
 */
export const authBeginLoading = () => ({
    type: 'AUTH_BEGIN_LOADING',
    payload: 'teste'
})

/**
 * Auth Error
 * @param {string} err
 * @return {object}
 */
export const authError = (err: string) => ({
    type: 'AUTH_SYSTEM_ERROR',
    payload: err
})

/**
 * Change Auth State
 * @param {string} value
 * @return {object}
 */
export const changeAuthState = (value: string) => ({
    type: 'AUTH_CHANGE_AUTH_STATE',
    payload: value
})

/**
 * Auth Sign In - Success
 * @param {CognitoUser} user
 * @return {object}
 */
export const authSignInSuccess = (user: CognitoUser) => {
    return {
        type: 'AUTH_SIGN_IN_SUCCESS',
        payload: user
    }
}

/**
 * Cognito - First Login
 * @param {string} email
 * @param {string} password
 * @return {void}
 */
export const authFirstLogin = () => ({
    type: 'AUTH_FIRST_LOGIN'
})

/**
 * Auth Forgot Password Success
 * @param {CognitoUser} user
 * @return {object} user
 */
export const fetchAuthedUserSuccess = (user: CognitoUser) => ({
    type: 'AUTH_FETCH_AUTHED_USER',
    payload: user
})

export const refreshTokens = (
    refreshToken: tokenType,
    user: CognitoUser,
    userBO: object
) => ({
    type: 'AUTH_REFRESH_TOKEN',
    payload: {
        token: refreshToken,
        user: user,
        userBO: userBO
    }
})

/**
 * Update User from Backoffice
 * @param {any} user
 * @return {object}
 */
export const updateUserBO = (user: any) => ({
    type: 'USER_BO_UPDATE',
    payload: user
})

//
// Async Opetarions - Redux Duck Pattern
//

/**
 * Sign Out - Logout
 * @return {void}
 */
export const signOut = () => {
    return async (dispatch: AppDispatch) => {
        // 1. Desloga a sessão do Cognito padrão
        try {
            Auth.configure(awsconfig)
            await Auth.signOut()
        } catch {
            // No SSO não há sessão Cognito; ignorar é o esperado.
        }

        // 2. Desloga a sessão do Cognito de trial
        try {
            Auth.configure(trialAwsConfig)
            await Auth.signOut({ global: false })
        } catch {
            // Ignorar se não houver sessão de trial ativa
        } finally {
            Auth.configure(awsconfig)
        }

        // 3. Varre e remove todas as chaves de autenticação do localStorage e sessionStorage
        if (typeof window !== 'undefined') {
            localStorage.removeItem(JU_TOKEN)
            localStorage.removeItem('JOTA_USER')
            localStorage.removeItem('JOTA_USER_TIMESTAMP')
            localStorage.removeItem('trial_email')
            localStorage.removeItem('area-select-form-trial')

            clearAllStorageCognitoAmplify()
            sessionStorage.clear()
        }

        // 4. Reseta a store Zustand do formulário de trial
        useFormTrialStore.getState().resetFormTrial?.()

        // 5. Atualiza o estado da aplicação no Redux para NOT_LOGGED
        dispatch(authInit('NOT_LOGGED'))
    }
}

/**
 * Sign In - Login
 * @param {string} email
 * @param {string} password
 * @param {string} newPassword - First Login
 * @param {boolean} preserve - preserve login?
 * @return {string}
 */
export const signIn = (email: string, password: string) => {
    return async (dispatch: AppDispatch) => {
        dispatch(authBeginLoading())
        try {
            const user = await Auth.signIn(email, password)

            if (user.challengeName === 'NEW_PASSWORD_REQUIRED') {
                return user.challengeName
            }

            dispatch(authSignInSuccess(user))
        } catch (err: unknown) {
            const typeError = err as ErrorAuthType
            dispatch(authError(typeError.message))
            return typeError.message
        }
    }
}

/**
 * Forgot Password
 * @param {string} email
 * @return {void}
 */
export const forgotPassword = (email: string) => {
    return async (dispatch: AppDispatch) => {
        dispatch(authBeginLoading())
        Auth.forgotPassword(email, { context: 'ju' })
            .then(() => dispatch(authForgotPasswordSuccess(email)))
            .catch((err) => dispatch(authError(err)))
    }
}

/**
 * Fetch Autheticated User
 * @return {void}
 */
export const fetchAuthedUser = () => {
    return async (dispatch: AppDispatch) => {
        dispatch(authBeginLoading())

        // Sessão SSO: usa JU_TOKEN em vez do Amplify
        const stored = localStorage.getItem(JU_TOKEN)
        if (stored) {
            try {
                const { idToken, email } = JSON.parse(stored)
                const userBO = await axiosJOTA.get(ProfileAPI.me(), {
                    headers: { Authorization: `Bearer ${idToken}` }
                })
                dispatch({
                    type: 'AUTH_SSO_SIGN_IN_SUCCESS',
                    payload: { idToken, email, userBO: userBO.data }
                })
            } catch {
                dispatch(authInit('NOT_LOGGED'))
            }
            return
        }

        try {
            const user: CognitoUser = await Auth.currentAuthenticatedUser()
            void Auth.currentSession().then((data) => {
                const dataToken: tokenType = {
                    idToken: {
                        jwtToken: data.getIdToken().getJwtToken()
                    },
                    accessToken: {
                        jwtToken: data.getAccessToken().getJwtToken()
                    },
                    refreshToken: {
                        token: data.getRefreshToken().getToken()
                    }
                }

                if (!dataToken.idToken.jwtToken) {
                    return
                }

                // Backoffice User data
                void axiosJOTA
                    .get(ProfileAPI.me(), {
                        headers: {
                            Authorization: `Bearer ${dataToken.idToken.jwtToken}`
                        }
                    })
                    .then((userBO) => {
                        dispatch(refreshTokens(dataToken, user, userBO.data))
                    })
            })
        } catch (error: unknown) {
            dispatch(authInit('NOT_LOGGED'))
        }
    }
}

/**
 * Forgot Password Submit
 * Description: In the forgot password email, the user received a code that it's necessary to be validated
 * @param {string} email
 * @param {string} code
 * @param {string} password
 * @return {void}
 */
export const forgotPasswordSubmit = (
    email: string,
    code: string,
    password: string
) => {
    return async (dispatch: AppDispatch) => {
        dispatch(authBeginLoading())
        try {
            await Auth.forgotPasswordSubmit(email, code, password)
            dispatch(forgotPasswordChangeSuccess())
        } catch (err: any) {
            dispatch(forgotPasswordChangeError(err.log))
        }
    }
}

/**
 * Reset Password Submit
 * @param {string} email
 * @param {string} password
 * @param {string} oldPassword
 * @return {boolean}
 */
export const resetPasswordSubmit = (
    email: string,
    password: string,
    oldPassword: string
) => {
    return async (dispatch: AppDispatch) => {
        dispatch(authBeginLoading())
        try {
            const user = await Auth.signIn(email, oldPassword)
            await Auth.completeNewPassword(user, password)
            await Auth.signOut()
            dispatch(forgotPasswordChangeSuccess())

            return true
        } catch (err) {
            dispatch(authFirstLogin())

            return false
        }
    }
}

export const ssoSignInSuccess = (idToken: string, email: string) => ({
    type: 'AUTH_SSO_SIGN_IN_SUCCESS',
    payload: { idToken, email }
})

export const completeSSOLogin = (idToken: string, email: string) => {
    return async (dispatch: AppDispatch) => {
        // Busca dados do usuário no backoffice
        try {
            const userBO = await axiosJOTA.get(ProfileAPI.me(), {
                headers: { Authorization: `Bearer ${idToken}` }
            })

            localStorage.setItem(JU_TOKEN, JSON.stringify({ idToken, email }))

            // Popula o Redux com os dados
            dispatch({
                type: 'AUTH_SSO_SIGN_IN_SUCCESS',
                payload: { idToken, email, userBO: userBO.data }
            })
        } catch (err) {
            localStorage.removeItem(JU_TOKEN)
            dispatch(authError('Falha ao buscar perfil'))
            throw err
        }
    }
}
/**
 * Decodifica o payload do JWT e compara exp * 1000 com Date.now()
 * Se o token não puder ser decodificado, retorna true (expirado),
 * evitando que um token corrompido seja tratado como válido
 * @param {string} token
 * @return {boolean} true se o token estiver expirado ou inválido, false se válido
 */
const isJwtExpired = (token: string): boolean => {
    try {
        const base64 = token.split('.')[1]
        const normalized = base64.replaceAll('-', '+').replaceAll('_', '/')
        // eslint-disable-next-line no-restricted-globals
        const binary = globalThis.atob(normalized)
        const bytes = Uint8Array.from(binary, (c) => c.codePointAt(0) ?? 0)
        const payload = JSON.parse(new TextDecoder().decode(bytes))
        return (
            typeof payload.exp === 'number' && payload.exp * 1000 < Date.now()
        )
    } catch {
        return true
    }
}

/**
 * Verifica se há sessão ativa: Cognito ou SSO (JU_TOKEN).
 * Use no lugar de Auth.currentAuthenticatedUser() nos componentes.
 * @return {Promise<void>} resolve se autenticado, rejeita se não
 */
export const checkActiveSession = async (): Promise<void> => {
    // Sessão SSO via JU_TOKEN
    try {
        const stored = localStorage.getItem(JU_TOKEN)

        if (!stored) {
            throw new Error('No token found')
        }

        const { idToken, email } = JSON.parse(stored)

        if (!idToken || !email) {
            throw new Error('Invalid token format')
        }

        if (!isJwtExpired(idToken)) {
            return
        }

        localStorage.removeItem(JU_TOKEN)
    } catch (err) {
        vercelLog(String(err))
    }

    // Sessão Cognito normal
    await Auth.currentAuthenticatedUser()
}
